import './RefinanciarWizard.scss';

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useTenant } from '../../context/TenantContext';
import { BackButton, ContinueButton } from '../../components/buttons/Buttons';
import EntradaMoneda from '../../components/currencyInput/EntradaMoneda';
import { postClientData } from '../../services/apiService';
import { saveToSessionStorage } from '../../services/storageService';
import { formatPhone, formatRUT, getYesterdayDate, isValidRUT } from '../../utils/formUtils';
import texts from '../../utils/texts.json';
import useTrackEvent from '../../utils/trackEvent';
import values from '../../utils/values.json';

const MINIMUM_AMOUNT = 600000;

const FormContacto = ({ onNextStep, data }) => {
  const { contactForm } = texts;
  const formRef = useRef(null);
  const hasPushedPageView = useRef(false);
  const { pushPageView, pushCustomEvent } = useTrackEvent();
  const navigate = useNavigate();
  const { isMeliTenant } = useTenant();

  const [formData, setFormData] = useState({
    rut: data?.rut || '',
    name: data?.name || '',
    surnames: data?.surnames || '',
    phone: data?.phone || '+56 9',
    email: data?.email || '',
    typeFinancing: 'refinanciamiento',
    type: 'CF',
    registrationNumber: data?.registrationNumber || '',
    workerType: data?.workerType || '',
    salary: data?.salary || '',
    startWorkingDate: data?.startWorkingDate || '',
  });

  const [inputErrors, setInputErrors] = useState({
    rut: '',
    name: '',
    surnames: '',
    phone: '',
    email: '',
    workerType: '',
    salary: '',
    startWorkingDate: '',
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!hasPushedPageView.current) {
      pushPageView(
        'vpv',
        'Refinanciamiento',
        '1',
        '/refinancia-tu-credito/info-personal',
        'Información personal'
      );

      hasPushedPageView.current = true;
    }
  }, []);

  const validPhone = (value) => {
    const phone = value.replace('+56', '').replace(/\s+/g, '');

    if (!value || value === '+56 9' || phone.length < 9) {
      return 'Por favor, ingresa un número de teléfono válido.';
    }

    return null;
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    let message = '';

    if (!value) {
      message = 'Campo requerido';
    } else {
      if (name === 'phone') {
        message = validPhone(value);
      }

      if (name === 'email') {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          message = 'Por favor, ingresa un correo electrónico válido.';
        }
      }

      if (name === 'startWorkingDate') {
        const selectedDate = new Date(value);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (selectedDate > today) {
          message = 'La fecha no puede ser futura.';
        } else {
          message = '';
        }
      }
    }

    setInputErrors((prevErrors) => ({
      ...prevErrors,
      [name]: message,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    pushCustomEvent('event_buttons', {
      flujo: 'Refinanciamiento',
      button_step_value: '1',
      step: '1',
      page: '/refinancia-tu-credito/info-personal',
      PageName: 'Información personal',
      button_step_name: 'continuar',
      version: 'v2',
    });

    setIsLoading(true);
    setError(null);

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      setIsLoading(false);
      return;
    }

    saveToSessionStorage('formData', formData);

    try {
      const response = await postClientData(formData);
      if (response && response.id) {
        sessionStorage.setItem('formData', JSON.stringify(response));

        onNextStep(response);
      } else {
        throw new Error('ID no encontrado en la respuesta de la API');
      }
    } catch (error) {
      setError('Error al enviar los datos, por favor inténtalo de nuevo.');
    } finally {
      setIsLoading(false);
    }
  };

  const allowedLetters = [
    'B',
    'C',
    'D',
    'F',
    'G',
    'H',
    'J',
    'K',
    'L',
    'P',
    'R',
    'S',
    'T',
    'V',
    'W',
    'X',
    'Y',
    'Z',
  ];

  const formatPatent = (value) => {
    let cleanValue = value.toUpperCase().replace(/[^A-Z0-9]/g, '');

    cleanValue = cleanValue
      .split('')
      .filter((char) => allowedLetters.includes(char) || !isNaN(char))
      .join('');

    if (cleanValue.length <= 6 && /^[A-Z]{2}/.test(cleanValue)) {
      return cleanValue.replace(/^([A-Z]{2})([0-9]{0,4})$/, '$1·$2');
    } else if (cleanValue.length > 6) {
      return cleanValue.replace(/^([A-Z]{2})([A-Z]{2})([0-9]{0,2})$/, '$1-$2·$3');
    }

    return cleanValue;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let formattedValue = value;

    if (name === 'rut') {
      formattedValue = formatRUT(value);
    }

    if (name === 'phone') {
      formattedValue = formatPhone(value);
    }

    if (name === 'registrationNumber') {
      formattedValue = formatPatent(value);
    }

    if (name === 'salary') {
      formattedValue = value.replace(/\D/g, '');
      formattedValue = formattedValue.slice(0, 8);
    }

    if (name === 'startWorkingDate') {
      const selectedDate = new Date(value);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (selectedDate > today) {
        setInputErrors((prev) => ({
          ...prev,
          startWorkingDate: 'La fecha no puede ser futura.',
        }));
      } else {
        setInputErrors((prev) => ({
          ...prev,
          startWorkingDate: '',
        }));
      }
    }

    setFormData((prevData) => ({ ...prevData, [name]: formattedValue }));
    handleBlur(e);
  };

  const validateForm = () => {
    if (!formData.rut || !isValidRUT(formData.rut)) {
      return 'Por favor, ingresa un RUT válido.';
    }
    if (validPhone(formData.phone)) {
      return 'Por favor, ingresa un número de teléfono válido.';
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email || !emailRegex.test(formData.email)) {
      return 'Por favor, ingresa un correo electrónico válido.';
    }
    if (!formData.name) {
      return 'Por favor, ingresa un nombre válido.';
    }
    if (!formData.surnames) {
      return 'Por favor, ingresa unos apellidos válidos.';
    }
    if (formData.salary) {
      const salaryValue = parseInt(String(formData.salary).replace(/\D/g, ''));

      if (salaryValue < MINIMUM_AMOUNT) {
        return `La renta mínima debe ser al menos ${MINIMUM_AMOUNT}`;
      }
    }

    return null;
  };

  return (
    <div className="refinanciar-wizard-contact-form">
      <h1>{contactForm.title}</h1>
      <p>{contactForm.subTitle}</p>
      <form ref={formRef} onSubmit={handleSubmit}>
        <div className="formControl-root">
          <label
            className={`inputLabel-root formLabel-root inputLabel-formControl inputLabel-outlined${
              inputErrors.rut ? '-input-error' : ''
            }`}
          >
            {contactForm.rut.text}
          </label>
          <div className="outlinedInput-root textField-root inputBase-root">
            <input
              name="rut"
              placeholder={contactForm.rut.placeholder}
              type="text"
              className={`form-input${inputErrors.rut ? '-input-error' : ''}`}
              value={formData.rut}
              onChange={handleInputChange}
              onBlur={handleBlur}
              required
            />
          </div>
          {inputErrors.rut && <span className="error-required-input-field">{inputErrors.rut}</span>}
        </div>
        <div className="formControl-root">
          <label
            className={`inputLabel-root formLabel-root inputLabel-formControl inputLabel-outlined${
              inputErrors.rut ? '-input-error' : ''
            }`}
          >
            {contactForm.name.text}
          </label>
          <div className="outlinedInput-root textField-root inputBase-root">
            <input
              name="name"
              placeholder={contactForm.name.placeholder}
              type="text"
              maxLength={30}
              className={`form-input${inputErrors.name ? '-input-error' : ''}`}
              value={formData.name}
              onChange={handleInputChange}
              onBlur={handleBlur}
              required
            />
          </div>
          {inputErrors.name && (
            <span className="error-required-input-field">{inputErrors.name}</span>
          )}
        </div>
        <div className="formControl-root">
          <label
            className={`inputLabel-root formLabel-root inputLabel-formControl inputLabel-outlined${
              inputErrors.surnames ? '-input-error' : ''
            }`}
          >
            {contactForm.lastName.text}
          </label>
          <div className="outlinedInput-root textField-root inputBase-root">
            <input
              name="surnames"
              placeholder={contactForm.lastName.placeholder}
              type="text"
              maxLength={60}
              className={`form-input${inputErrors.surnames ? '-input-error' : ''}`}
              value={formData.surnames}
              onChange={handleInputChange}
              onBlur={handleBlur}
              required
            />
          </div>
          {inputErrors.surnames && (
            <span className="error-required-input-field">{inputErrors.surnames}</span>
          )}
        </div>
        <div className="formControl-root">
          <label
            className={`inputLabel-root formLabel-root inputLabel-formControl inputLabel-outlined${
              inputErrors.phone ? '-input-error' : ''
            }`}
          >
            {contactForm.celPhone.text}
          </label>
          <div className="outlinedInput-root textField-root inputBase-root">
            <input
              type="tel"
              name="phone"
              placeholder={contactForm.celPhone.placeholder}
              className={`form-input${inputErrors.phone ? '-input-error' : ''}`}
              value={formData.phone}
              onChange={handleInputChange}
              onBlur={handleBlur}
              maxLength="17"
              required
            />
          </div>
          {inputErrors.phone && (
            <span className="error-required-input-field">{inputErrors.phone}</span>
          )}
        </div>
        <div className="formControl-root">
          <label
            className={`inputLabel-root formLabel-root inputLabel-formControl inputLabel-outlined${
              inputErrors.email ? '-input-error' : ''
            }`}
          >
            {contactForm.email.text}
          </label>
          <div className="outlinedInput-root textField-root inputBase-root">
            <input
              type="email"
              onChange={handleInputChange}
              onBlur={handleBlur}
              value={formData.email}
              name="email"
              placeholder={contactForm.email.placeholder}
              className={`form-input${inputErrors.email ? '-input-error' : ''}`}
            />
          </div>
          {inputErrors.email && (
            <span className="error-required-input-field">{inputErrors.email}</span>
          )}
        </div>
        <div className="formControl-root">
          <label
            className={`inputLabel-root formLabel-root inputLabel-formControl inputLabel-outlined${
              inputErrors.workerType ? '-input-error' : ''
            }`}
          >
            {contactForm.employmentRelationship.text}
          </label>
          <div className="outlinedInput-root textField-root inputBase-root">
            <select
              name="workerType"
              className={`worker-type${inputErrors.workerType ? '-input-error' : ''}`}
              value={formData.workerType}
              onChange={handleInputChange}
              onBlur={handleBlur}
              required
            >
              <option value="" disabled hidden>
                Seleccionar
              </option>
              {values.ocupacion.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          {inputErrors.workerType && (
            <span className="error-required-input-field">{inputErrors.workerType}</span>
          )}
        </div>
        <div className="formControl-root">
          <label
            className={`inputLabel-root formLabel-root inputLabel-formControl inputLabel-outlined${
              inputErrors.salary ? '-input-error' : ''
            }`}
          >
            {contactForm.netIncome.text}
          </label>
          <div className="outlinedInput-root textField-root inputBase-root">
            <EntradaMoneda
              type="text"
              name="salary"
              placeholder={contactForm.netIncome.placeholder}
              min={600000}
              className={`form-input${inputErrors.salary ? '-input-error' : ''}`}
              value={formData.salary}
              onChange={handleInputChange}
              onBlur={handleBlur}
              required
            />
          </div>
          {inputErrors.salary && (
            <span className="error-required-input-field">{inputErrors.salary}</span>
          )}
        </div>
        <div className="formControl-root">
          <label
            className={`inputLabel-root formLabel-root inputLabel-formControl inputLabel-outlined${
              inputErrors.startWorkingDate ? '-input-error' : ''
            }`}
          >
            {contactForm.employmentStartDate.text}
          </label>
          <div className="outlinedInput-root textField-root inputBase-root">
            <input
              type="date"
              name="startWorkingDate"
              className={`form-input${inputErrors.startWorkingDate ? '-input-error' : ''}`}
              value={formData.startWorkingDate}
              onChange={handleInputChange}
              onBlur={handleBlur}
              max={getYesterdayDate()}
              required
            />
          </div>
          {inputErrors.startWorkingDate && (
            <span className="error-required-input-field">{inputErrors.startWorkingDate}</span>
          )}
        </div>
        <div className="formControl-root">
          <label className="inputLabel-root formLabel-root inputLabel-formControl inputLabel-outlined">
            {contactForm.vehicleLicensePlate.text}
          </label>
          <div className="outlinedInput-root textField-root inputBase-root">
            <input
              onChange={handleInputChange}
              value={formData.registrationNumber}
              className="form-input"
              placeholder={contactForm.vehicleLicensePlate.placeholder}
              type="text"
              name="registrationNumber"
            />
          </div>
        </div>

        {error && <div className="error-msg">{error}</div>}

        <div className="nav-button-container">
          <BackButton onClick={() => navigate(-1)} />
          <ContinueButton type="submit" isLoading={isLoading} isMeliTenant={isMeliTenant} />
        </div>
      </form>
    </div>
  );
};

export default FormContacto;
